/**
 * Wire constants for the OpenClaw Gateway handshake.
 *
 * `client.id` and `client.mode` are closed enums on the Gateway
 * (`GatewayClientIdSchema` / `GatewayClientModeSchema`), so a client outside the
 * OpenClaw tree cannot introduce its own identifier and has to pick the class it
 * actually belongs to.
 *
 * That class is the browser operator UI. This app manages sessions — renaming,
 * pinning, archiving, deleting — which is Control UI work, not webchat work:
 * Gateways restrict `webchat` clients to sending messages and reject
 * `sessions.patch` from them outright. The identity keeps the browser origin
 * check that applies to any browser-hosted operator UI; it does not opt out of
 * anything the previous `webchat` identity was subject to.
 */
export const CLIENT_ID = "openclaw-control-ui";
export const CLIENT_MODE = "ui";
export const CLIENT_DISPLAY_NAME = "cllw";
export const CLIENT_VERSION = "0.1.0";

/** Operator role: this app drives sessions, it does not host agent work. */
export const CONNECT_ROLE = "operator";

/**
 * Scopes requested at connect.
 *
 * `operator.admin` is included because deleting a conversation needs it on any
 * Gateway without the archive-then-delete contract, and a messenger whose
 * delete button does not delete is worse than one that asks for more up front.
 * The Gateway still gates this behind an explicit pairing approval.
 */
export const CONNECT_SCOPES = [
  "operator.read",
  "operator.write",
  "operator.admin",
] as const;

export const MIN_PROTOCOL = 4;
export const MAX_PROTOCOL = 4;

/** Gateway default from `openclaw gateway` — prefilled in the setup form. */
export const DEFAULT_GATEWAY_URL = "ws://127.0.0.1:18789";

export const RECONNECT = { initialMs: 500, multiplier: 1.7, maxMs: 20_000 } as const;

/** Transcript page size for the initial history load of a conversation. */
export const HISTORY_PAGE_SIZE = 80;

/** Session index page size; the list is virtual-free and stays comfortably small. */
export const SESSION_PAGE_SIZE = 100;

/** Hits per message search. The Gateway caps this at 25. */
export const SEARCH_PAGE_SIZE = 20;

/** Rows fetched per "load older messages" step. */
export const HISTORY_MORE_PAGE_SIZE = 60;
